import React, { CSSProperties } from "react";
import { dynamicGridGroupBy } from "../components/BuilderInterface";
import {
    buildSizes,
    QueryFieldsCustomers,
    QueryFieldsProducts,
    QueryOrderByOptionsProducts,
    SupportedBarcodeTypes
} from "./constants";
import { DividerPatternType } from "../components/BuilderDivider/patterns/DividerPatterns";
import { DynamicDataKey } from "./dynamicData";
import { ConditionFilterFunctionKeys } from "./filterChildren";
import { DynamicDataContext } from "./activeEntityType";


export type DataType = "products" | "customers" | "orders";

//need to redo these types, they are kind of cryptic
export type QueryOrderByOptions = {
    products: QueryOrderByOptionsProducts;
    customers: "firstName A-Z" | "lastName A-Z";
    orders: "createdBy" | "updatedAt";
};

type QueryDataTypes = "products" | "customers" | "orders";

export enum OrderQueriesKey {
    ORDER_SOURCE = "orderSource",
    ORDER_STATUS = "orderStatus",
    ORDER_OUTLET = "orderOutlet",
    ORDER_STATION = "orderStation",
    ORDER_CUSTOMER = "orderCustomer",
    ORDER_SERVED_BY = "orderServedBy",
    ORDER_NUMBER = "orderNumber",
    ORDER_DATE_RANGE = "orderDateRange"
}

export enum CustomerQueriesKey {
    CUSTOMER_EMAIL = "customerEmail",
    CUSTOMER_FIRST_NAME = "customerFirstName",
    CUSTOMER_LAST_NAME = "customerLastName",
    CUSTOMER_PHONE = "customerPhone",
    CUSTOMER_SOURCE = "customerSource",
    CUSTOMER_CREATED_AT_OUTLET = "customerCreatedAtOutlet",
    CUSTOMER_LAST_ACTIVE_MONTHS_AGO = "customerLastActiveMonthsAgo",
    CUSTOMER_EVER_ACTIVE_IN_OUTLET = "customerEverActiveInOutlet"
}

export type orderStatus = "all" | "completed" | "refunded" | "voided" | "partial-refund" | "layaway" | "parked" | "in-cart";

export type OrderQueries = {
    [OrderQueriesKey.ORDER_SOURCE]?: "all" | "in-store" | "woo-commerce";
    [OrderQueriesKey.ORDER_STATUS]?: orderStatus;
    [OrderQueriesKey.ORDER_OUTLET]?: "all" | "current" | string;
    [OrderQueriesKey.ORDER_STATION]?: "all" | "current" | string;
    [OrderQueriesKey.ORDER_CUSTOMER]?: "all" | "current" | string;
    [OrderQueriesKey.ORDER_SERVED_BY]?: "all" | "current" | string;
    [OrderQueriesKey.ORDER_NUMBER]?: "all" | string;
    [OrderQueriesKey.ORDER_DATE_RANGE]?: "all" | "thisYear" | "thisMonth" | "thisWeek" | "last30Days";
};

export type CustomerQueries = {
    [CustomerQueriesKey.CUSTOMER_EMAIL]?: string;
    [CustomerQueriesKey.CUSTOMER_FIRST_NAME]?: string;
    [CustomerQueriesKey.CUSTOMER_LAST_NAME]?: string;
    [CustomerQueriesKey.CUSTOMER_PHONE]?: string;
    [CustomerQueriesKey.CUSTOMER_SOURCE]?: "all" | "in-store" | "thirdParty"; // TODO_J: Should we use the CustomerPlatform enum?
    [CustomerQueriesKey.CUSTOMER_CREATED_AT_OUTLET]?: "all" | "current" | string;
    [CustomerQueriesKey.CUSTOMER_LAST_ACTIVE_MONTHS_AGO]?: number;
    [CustomerQueriesKey.CUSTOMER_EVER_ACTIVE_IN_OUTLET]?: "all" | "current" | string;
};

export type BuildNodeQuery<T extends QueryDataTypes> = {
    dataType: T;
    order?: {
        orderBy: QueryOrderByOptions[T];
        orderDirection: "ascending" | "descending";
    };
    limit: number;
} & (T extends "orders"
    ? {
          queries: OrderQueries;
      }
    : T extends "customers"
      ? {
            queries: CustomerQueries;
        }
      : {
            match?: {
                field: QueryFieldsProducts | QueryFieldsCustomers;
                criteria: string[] | boolean;
            };
        });

export enum NodeStates {
    DEFAULT = "default",
    HOVER = "hover",
    ACTIVE = "active",
    DISABLED = "disabled",
    FOCUS = "focus",
    SELECTED = "selected",
    CUSTOM = "custom"
}

export type StateManager = {
    [key in NodeStates]?: {
        [key: string]: React.CSSProperties | string[] | string;
    };
};

export type ConditionalVariation = {
    condition: string;
    id?: string;
    children?: string[];
    nodeProps?: Record<string, any>;
    conditionalVariations?: ConditionalVariation[];
};

export interface BaseTile {
    id: string;
    index: number;
    type: "product" | "action" | "category" | "folder" | "back" | "empty";
    data: {
        tileData: string;
        folderId?: string;
        [key: string]: any;
    };
}

export interface BuildNode {
    id: string;
    name: string;
    customName?: string;
    type: "component" | "global-block" | "simpleCart" | "repeater" | "divider";
    attr?: BuildNodeAttributes;
    text?: string;
    input?: {
        style?: React.CSSProperties;
        placeholder?: string;
        label?: string;
        labelStyle?: React.CSSProperties;
        toggleStyle?: React.CSSProperties;
    };
    backgroundImage?: string;
    customCSS?: React.CSSProperties;
    globalBlock?: string;
    query?: BuildNodeQuery<DataType>;
    parent?: string | null;
    children?: string[]; //list of ids
    childrenFilterKey?: ConditionFilterFunctionKeys; //function to filter children and only show one of them
    previewChildIndex?: number; //for builder only
    actions?: Action[]; // condition action or normal render actions [add product etc..]
    actionsBuilder?: string[]; //list of ids for action-builder actions
    dynamicData?: DynamicDataKey; //dynamic data key
    conditions?: Condition[];
    nodeSize?: { width: NodeSizes; height: NodeSizes };
    isPremium?: boolean;
    stateManager?: StateManager;
    noDelete?: boolean;
    noCopy?: boolean;
    noMove?: boolean;
    conditionalVariations?: ConditionalVariation[];
    conditionType?: string;
}

export interface DynamicGridNode extends BuildNode {
    groupBy?: dynamicGridGroupBy;
    dividerStyles?: React.CSSProperties;
}

export interface PaymentGridNode extends BuildNode {
    buttonStyles?: CSSProperties;
}

export interface TemplateDiv extends BuildNode {
    customName?: string;
    previewImage?: string;
    tag?: string;
}

export interface LinkedInputDisplayNode extends BuildNode {
    priceStyle?: React.CSSProperties;
    quantityStyle?: React.CSSProperties;
}

export interface SimpleCartNode extends BuildNode {
    type: "simpleCart";
    prepend?: { style: React.CSSProperties; value: string };
    append?: { style: React.CSSProperties; value: string };
    fallback?: { style: React.CSSProperties; value: string };
    expandedNode: SimpleCartExpandedNode;
}

export interface DividerNode extends BuildNode {
    pattern: DividerPatternType;
    isVertical?: boolean;
    patternStyle?: {
        color?: string;
        gap?: number;
    };
}

export interface CustomerNotesNode extends BuildNode {
    title?: {
        text: string;
        style: React.CSSProperties;
    };
    addNoteButton?: {
        text: string;
        style: React.CSSProperties;
        icon?: {
            src: string;
            style: React.CSSProperties;
            colors?: string[];
        };
    };
    dateText?: {
        style: React.CSSProperties;
    };
    timeText?: {
        style: React.CSSProperties;
    };
    deleteNoteIcon?: {
        src: string;
        style: React.CSSProperties;
        colors?: string[];
    };
    dividerStyle?: React.CSSProperties;
    noteStyle?: React.CSSProperties;
}
export interface SimpleCartExpandedNode extends BuildNode {
    mainBg: string;
    totalBg: string;
    cartName: string;
    cartWidth: string;
    cartHeight?: string;
    showTaxes: boolean;
    customizeTaxes?: string;
    showTotal?: boolean;
    showCartSubtotal?: boolean;
    customizeCartSubtotal?: string;
    showCartDiscount?: boolean;
    customizeCartDiscount?: string;
    showCartTotal?: boolean;
    customizeCartTotal?: string;
    showQuantity?: boolean;
    balanceDueLabel?: string;
    showBalance?: boolean;
    dividerColor: string;
    showItemUnit?: boolean;
    showItemUnitLabel?: string;
    showNote?: boolean;
    showVariationDetails?: boolean;
    showSku?: boolean;
    showSkuLabel?: string;
    showDeleteIcon?: boolean;
    showDiscountAndFees?: boolean;
    showOrderNotes?: boolean;
    showOrderNotesLabel?: boolean;
    orderNotesLabel?: string;
    orderTotalLabel?: string;
    showOrderTotal?: boolean;
    mode: "left" | "right";
    openMode?: "pushLeft" | "pushRight" | "overlayLeft" | "overlayRight";
    openCartIcon?: {
        src: string;
        style?: React.CSSProperties;
        colors?: string[];
    };
    icon?: {
        src: string;
        style?: React.CSSProperties;
        colors?: string[];
    };
    closeIcon?: {
        src: string;
        style?: React.CSSProperties;
        colors?: string[];
    };
    typography?: {
        openCart?: React.CSSProperties;
        cartName?: React.CSSProperties;
        product?: React.CSSProperties;
        productSubtext?: React.CSSProperties;
        total?: React.CSSProperties;
        subtotals?: React.CSSProperties;
        orderTotal?: React.CSSProperties;
        customer?: React.CSSProperties;
        orderNote?: React.CSSProperties;
        orderNoteLabel?: React.CSSProperties;
        summarySubtext?: React.CSSProperties;
    };
    showActiveCustomer?: boolean;
    customerChip?: CSSProperties;
    deleteItemIcon?: {
        src?: string;
        colors?: string[];
        style?: React.CSSProperties;
    };
    customerIcon?: {
        src?: string;
        colors?: string[];
        style?: React.CSSProperties;
    };
    customerXIcon?: {
        src?: string;
        colors?: string[];
        style?: React.CSSProperties;
    };
}

export enum PopupAlignment {
    LEFT = "left",
    CENTER = "center",
    RIGHT = "right"
}

export interface PopupNode extends BuildNode {
    backdropClose: boolean;
    alignment: PopupAlignment;
    wrapperStyle?: React.CSSProperties;
    closeButton?: {
        active: boolean;
        style?: React.CSSProperties;
        icon?: {
            src?: string;
            style?: React.CSSProperties;
            colors?: string[];
        };
    };
}

export interface TextNode extends BuildNode {
    fallback?: {
        value: string;
        style?: React.CSSProperties;
    };
    append?: {
        value: string;
        style?: React.CSSProperties;
    };
    prepend?: {
        value: string;
        style?: React.CSSProperties;
    };
    characterLimit?: number;
    timeFormat?: TimeFormat;
}

export type TimeFormat =
    | "relative"
    | "HH:mm"
    | "HH:mm:ss"
    | "hh:mm a"
    | "hh:mm:ss a"
    | "dd/MM/yyyy"
    | "MM/dd/yyyy"
    | "MM/dd/yyyy HH:mm"
    | "PPPP"
    | "PP"
    | "p"
    | "Pp"
    | "yyyy-MM-dd"
    | "yyyy-MM-dd HH:mm:ss"
    | "do MMMM, yyyy"
    | "MMMM do, yyyy"
    | "dd MMM yyyy"
    | "EEE, MMM d, yyyy"
    | "dd-MM-yyyy HH:mm";

export interface DivNode extends BuildNode {
    doLoading?: boolean;
}

export interface DividerNode extends BuildNode {
    pattern: DividerPatternType;
    isVertical?: boolean;
    patternStyle?: {
        color?: string;
        gap?: number;
    };
}

export interface ButtonNode extends TextNode {
    icon?: {
        src: string;
        style: React.CSSProperties;
        colors?: string[];
    };
}

export enum VismaButtonNodeTypes {
    GO_TO_INDEX = "goToIndex",
    ENTER_MANUAL_INPUT = "enterManualInput",
    CLOCK_IN = "clockIn",
    CLOCK_OUT = "clockOut"
}

export interface VismaButtonNode extends ButtonNode {
    vismaButtonType?: VismaButtonNodeTypes;
}

export interface VismaButtonListNode extends BuildNode {
    buttonStyle?: React.CSSProperties;
    vismaButtonType?: VismaButtonNodeTypes;
}

export interface VismaInputNode extends BuildNode {
    input?: {
        placeholder?: string;
        label?: string;
        labelStyle?: React.CSSProperties;
        style?: React.CSSProperties;
    };
}

export interface IconNode extends BuildNode {
    icon?: {
        src: string;
        style?: React.CSSProperties;
        colors?: string[];
    };
}

export interface ReceiptNode extends BuildNode {
    signature?: {
        label?: string;
        labelStyle?: React.CSSProperties;
        subtext?: string;
        subtextStyle?: React.CSSProperties;
    };
    styles?: {
        productTitle?: React.CSSProperties;
        productPrice?: React.CSSProperties;
        sku?: React.CSSProperties;
        productVariation?: React.CSSProperties;
        productDescription?: React.CSSProperties;
        productDiscount?: React.CSSProperties;
        productTax?: React.CSSProperties;
        productTotal?: React.CSSProperties;
        productTotalPrice?: React.CSSProperties;
        cartTotal?: React.CSSProperties;
        cartPayment?: React.CSSProperties;
        cartEMV?: React.CSSProperties;
    };
    settings: {
        showValues: boolean;
        showVariations: boolean;
        showNotes: boolean;
        showSku: boolean;
        showDescription: boolean;
        showTips: boolean;
        showProductTaxes: boolean;
        showProductDiscount: boolean;
        showProductFee: boolean;
        showCartFee: boolean;
        showCartTaxes: boolean;
        showCartDiscount: boolean;
        showSignature: boolean;
    };
}

export interface DropdownNode extends BuildNode {
    dropdownOptionStyle?: React.CSSProperties;
    optionWrapperStyle?: React.CSSProperties;
    previewOptions?: Record<string, string>;
}

export interface Margins {
    top: string;
    right: string;
    bottom: string;
    left: string;
}
export interface CashLedgerNode extends BuildNode {
    settings: {
        showTime: boolean;
    };
}

export interface EOSNode extends BuildNode {
    settings: {
        showSessionSummary: boolean;
        showPaymentBreakdown: boolean;
        showCashDrawer: boolean;
        showFloatAdjustment: boolean;
        showTaxBreakdown: boolean;
        showRefunds: boolean;
        showStaffBreakdown: boolean;
        showDiscount: boolean;
        showClosingNotes: boolean;
    };
}
/** Fixed amount vs percentage. Use "fixed" | "percent" for new data. Legacy "%" | "$" is supported when reading. */
export type AmountType = "fixed" | "percent";

export function isPercentAmountType(type: string | undefined): boolean {
    return type === "percent" || type === "%";
}

export interface TipsSettings {
    request: boolean;
    customTip1?: { label: string; amount: { value: string; type: AmountType } };
    customTip2?: { label: string; amount: { value: string; type: AmountType } };
    customTip3?: { label: string; amount: { value: string; type: AmountType } };
    allowCustomTips?: boolean;
    allowNoTips?: boolean;
}
export interface PaymentDetails {
    addedFee?: { value: string; type: AmountType };
    addedDiscount?: { value: string; type: AmountType };
    tips?: TipsSettings;
    cashRounding?: "" | "0.05" | "0.10" | "0.25" | "0.50" | "1.00";
    changeCalculator?: boolean;
    terminal?: "cloud" | "bluetooth";
    checkoutCompletePage?: string;
    name?: string;
    paymentName?: string;
}
export interface PaymentButtonNode extends ButtonNode {
    paymentDetails?: PaymentDetails;
}
export interface PrintButtonNode extends ButtonNode {
    printerGlobalBlock?: string;
}
export interface ProductQuantityNode extends BuildNode {
    buttonStyle?: React.CSSProperties;
    iconStyle?: React.CSSProperties;
    inputTypography?: React.CSSProperties;
    target?: string; //in case it's not for Active Product, but for specific target (for example in customSale)
    icons?: {
        decrement?: {
            src?: string;
            style?: React.CSSProperties;
            colors?: string[];
        };
        increment?: {
            src?: string;
            style?: React.CSSProperties;
            colors?: string[];
        };
    };
}

export interface ProductVariationsNode extends BuildNode {
    optionWrapperStyles?: React.CSSProperties;
    inputStyles?: React.CSSProperties;
    activeInputStyles?: React.CSSProperties;
    titleStyles?: React.CSSProperties;
    groupFontStyles?: React.CSSProperties;
    showPrices?: boolean;
    noVariations?: BuildNode;
}

export interface CustomNumpadNode extends BuildNode {
    keyStyle?: React.CSSProperties;
    target?: string;
    deleteIcon?: {
        src?: string;
        style?: React.CSSProperties;
        colors?: string[];
    };
    gridConfig?: {
        columns: number;
    };
}

export interface ImageNode extends BuildNode {
    image?: {
        id: string;
        name: string;
        url: string;
        style?: React.CSSProperties;
        urlFallback?: string; //for if dynamic data is missing, but not if there is no internet.
    };
}

export interface Barcode {
    bcid: SupportedBarcodeTypes;
    content: string;
    prepend?: string;
    append?: string;
    color?: string;
    displayText?: boolean;
}

export interface BarcodeNode extends BuildNode {
    barcode?: Barcode;
    svg?: string;
}

export interface IframeNode extends BuildNode {
    //when offline
    image?: {
        id: string;
        name: string;
        url: string;
    };
    iframeUrl?: string;
    isOffline?: boolean;
}

export interface SearchNode extends BuildNode {
    // input?: {
    //     placeholder?: string;
    //     placeholderStyle?: React.CSSProperties;
    //     style?: React.CSSProperties;
    //     wrapperStyle?: React.CSSProperties;
    // };
    resultStyle?: React.CSSProperties;
    included?: {
        products?: boolean;
        customers?: boolean;
        orders?: boolean;
    };
    icons?: {
        search?: {
            src: string;
            style: React.CSSProperties;
            colors?: string[];
        };
        products?: {
            src: string;
            style: React.CSSProperties;
            colors?: string[];
        };
        customers?: {
            src: string;
            style: React.CSSProperties;
            colors?: string[];
        };
        orders?: {
            src: string;
            style: React.CSSProperties;
            colors?: string[];
        };
    };
}

export interface SearchFieldNode extends FilterNode {
    searchIcon?: {
        src: string;
        style: React.CSSProperties;
        colors?: string[];
    };
    clearIcon?: {
        src: string;
        style: React.CSSProperties;
        colors?: string[];
    };
}

export interface MenuLink {
    id: string;
    link: string;
    name: string;
    icon?: {
        src: string;
        style: React.CSSProperties;
        colors?: string[];
    };
    typography?: React.CSSProperties;
}

export interface MenuNode extends BuildNode {
    menu: {
        links: MenuLink[];
        linkStyle?: React.CSSProperties;
        activeLinkStyle?: React.CSSProperties;
        iconStyle?: React.CSSProperties;
        activeIconStyle?: React.CSSProperties;
        itemLayout?: React.CSSProperties;
    };
}

export interface FilterNode extends BuildNode {
    filter?: {
        target?: string;
        fields?: any;
        typeOfFilter?: QueryFieldsProducts | keyof OrderQueries;
    };
    previewOptions?: Record<string, string>;
}

export interface RadioFieldNode extends FilterNode {
    wrap?: boolean;
    radioStyle?: React.CSSProperties;
    activeRadioStyle?: React.CSSProperties;
}

export interface SlideoutNode extends BuildNode {
    slideout?: {
        openMode?: "pushLeft" | "pushRight" | "overlayLeft" | "overlayRight";
        header?: {
            style?: React.CSSProperties;
            title?: {
                text?: string;
                style?: React.CSSProperties;
            };
            icon?: {
                src?: string;
                style?: React.CSSProperties;
                colors?: string[];
            };
            closeButton?: {
                visible?: boolean;
                icon?: {
                    src?: string;
                    style?: React.CSSProperties;
                    colors?: string[];
                };
            };
        };
    };
}

export enum SmartTileTypes {
    PRODUCT = "product",
    ACTION = "action",
    CATEGORY = "category",
    FOLDER = "folder",
    BACK = "back",
    EMPTY = "empty"
}

export interface SmartGridNode extends BuildNode {
    smartGrid?: {
        columns?: number;
        rows?: number;
        allowedTypes?: SmartTileTypes[];
        allowedRoles?: string[];
        style?: React.CSSProperties;
    };
    button?: {
        icon?: {
            src: string;
            style: React.CSSProperties;
            colors?: string[];
        };
        style?: React.CSSProperties;
    };
}

export interface SmartTileNode extends BuildNode {
    actionTileActions?: string[];
    productTileAction?: string[];
    query?: BuildNodeQuery<"products">;
    tileData?: BaseTile;
}

export interface EmbedSectionNode extends BuildNode {
    embededSection?: "orders" | "customers" | "refunds";
}

export interface BuildNodeAttributes {
    [key: string]: BuildNodeAttributeTypes;
    style?: CSSProperties;
    className?: string;
}
export type BuildNodeAttributeTypes = string | { [key: string]: string } | undefined | CSSProperties;

export interface BaseLayer {
    id: string;
    name: string;
    root: string;
    components: { [id: string]: BuildNode };
}

export enum GlobalBlockTypes {
    GB_PRINT = "globalBlockPrint",
    GB_CFD = "globalBlockCFD",
    GB_GRID_CARD = "globalBlockGridCard",
    GB_CI = "globalBlockCI"
}

export enum BuildTypes {
    BUILD = "build",
    TEMPLATE = "template",
    OLIVER_TEMPLATE = "oliverTemplate",
    ORG_TEMPLATE = "orgTemplate",
    GLOBAL_BLOCK = "globalBlock",
    OLIVER_THEME = "oliverTheme",
    OLIVER_GLOBAL_BLOCK = "oliverGlobalBlock",
    ORG_THEME = "orgTheme",
    ORG_GLOBAL_BLOCK = "orgGlobalBlock"
}

export enum NodeSizes {
    FILL = "fill",
    FIT = "fit",
    CUSTOM = "custom",
    SCROLL = "scroll"
}

export type SpecialLayers = {
    homepage?: { id: string; name: string; type: BaseLayerTypes };
    checkoutComplete?: { id: string; name: string; action?: string };
    customerDisplayHomepage?: { id: string; name: string; type: BaseLayerTypes };
};

export enum BaseLayerTypes {
    PAGES = "pages",
    POPUPS = "popups",
    CFDS = "cfds"
}

export interface Build {
    id: string;
    name?: string;
    version?: string;
    size: { key: BuildSizes; frameSize?: { x: number; y: number } };
    pages: BaseLayer[];
    popups: BaseLayer[];
    cfds: BaseLayer[];
    specialLayers?: SpecialLayers;
    previewImage?: string;
    companyId?: string;
    accountId?: string;
    buildType?: BuildTypes;
    globalBlockType?: GlobalBlockTypes;
    actions?: string[]; //list of ids for action-builder actions
    actionBuilder?: { [id: string]: ActionBuilder }; //actions master look-up dictionary
    globalBlocks?: { [gBId: string]: { name: string; nodes: string[] } };
    extraAttributes?: BuildExtraAttributes;
    sourceId?: string;
    isPremium?: boolean;
    paymentGridSettings?: {
        allowRename: boolean;
        allowCustom: boolean;
    };
    _id?: string;
    globalBlockIds?: string[];
    isPublished?: boolean;
    tag?: string;
}

export interface BuildUpdateInfo {
    updatedBy: string | null;
    updatedAt: string | null;
}

export interface DynamicField {
    id: string;
    name: string;
    type: DynamicDataContext | null;
    value: string;
    dynamicData?: string | null;
}

export interface BuildExtraAttributes {
    fonts?: Font[];
    buildGlobalColors?: { id: string; name: string; color: string }[];
    decimals?: number;
    characterLimit?: number;
}
export interface Font {
    name: string;
    url?: string;
}

export interface ActionBuilder {
    name: string;
    id: string;
    isAuto: boolean;
    trigger: { condition: string } | null;
    actions: RenderActionBuilderItem[];
    hidden?: boolean;
}

export interface RenderActionBuilderItem {
    type: string;
    id: string;
    action: BuilderAction;
}

export type BuilderAction = {
    type?: ActionType;
    data?: any;
    target?: string;
    condition?: string;
    message?: string;
    name?: string;
} | null;

export enum ActionName {
    ON_CLICK = "onClick",
    ON_CHANGE = "onChange",
    ON_MOUSE_OUT = "onMouseOut",
    ON_MOUSE_OVER = "onMouseOver",

    ON_BLUR = "onBlur",
    ON_FOCUS = "onFocus",

    ON_KEYUP = "onKeyUp",
    ON_KEYDOWN = "onKeyDown",

    ON_LOAD = "onLoad",
    ON_RESIZE = "onResize",
    ON_SCROLL = "onScroll",
    ON_DRAG = "OnDrag",
    ON_DROP = "onDrop",
    ON_ERROR = "onError",
    ON_CONDITION = "onCondition"
}

export type BuildSizes = keyof typeof buildSizes | "custom";

export type FrameSizeObject = {
    label: string;
    value: { x: number; y: number | "fit-content" };
    grouping: "1" | "2";
    type: null | "Desktop" | "Tablet" | "Mobile" | GlobalBlockTypes.GB_PRINT;
    class?: string;
    noRotate?: boolean;
};

export enum ActionType {
    NAVIGATE_TO_PAGE = "navigateToPage",
    NAVIGATE_TO_PAGE_CFD = "navigateToPageCFD",
    TOGGLE_SLIDEOUT = "toggleSlideOut",
    NAVIGATE_TO_URL = "navigateToUrl",
    NAVIGATE_TO_HOME = "navigateToHome",
    OPEN_CASH_DRAWER = "openCashDrawer",
    NAVIGATE_TO_ORDERS = "navigateToOrders",
    NAVIGATE_TO_CASH_MANAGEMENT = "navigateToCashManagement",
    CART_ADD_NOTE = "cartAddNote",
    CART_REMOVE_NOTE = "cartRemoveNote",
    CART_ADD_FEE = "cartFee",
    CART_ADD_PRODUCT = "cartAddProduct",
    CLEAR_CART = "clearCart",
    CLEAR_ACTIVE_PRODUCT = "clearActiveProduct",
    CART_ADD_DISCOUNT = "cartAddDiscount",
    CART_REMOVE_PRODUCT = "cartRemoveProduct",
    CART_REMOVE_DISCOUNT = "cartRemoveDiscount",
    CART_SHOW_DISCOUNT_UI = "cartShowDiscountUI",
    CART_SHOW_PROD_DISCOUNT_UI = "cartShowProdDiscountUI",
    CART_ADD_SPECIFIC_PRODUCT = "cartAddSpecificProduct",
    REFUND_SELECTED_ORDER = "refundSelectedOrder",
    REFUND_SCANNED_ORDER = "refundScannedOrder",
    SET_PRODUCT_ACTIVE = "setProductActive",

    PARK_ORDER = "parkOrder",
    VIEW_PARK_ORDER = "viewParkOrder",
    RESUME_PARK_ORDER = "resumeParkOrder",
    DELETE_PARK_ORDER = "deleteParkOrder",
    RE_ORDER = "reOrder",

    PROD_ADD_DISCOUNT = "productDiscount",
    PROD_ADD_NOTE = "productAddNote",
    PROD_ADD_FEES = "productAddFees",

    CUSTOMER_ADD_NOTE = "customerAddNote",
    CUSTOMER_CLEAR_ACTIVE = "customerClearActive",
    CUSTOMER_ADD_CART = "customerAddCart",

    OPEN_MODAL = "openModal",
    CLOSE_MODAL = "closeModal",

    PRINT = "print",
    WEBHOOK = "webhook",
    ZAPIER = "zapier",

    SHOW_PROD_NOTE_UI = "showProdNoteUI",
    SHOW_PROD_FEES_UI = "showProdFeeUI",
    SHOW_ORDER_NOTE_UI = "showOrderNoteUI",
    SHOW_ADD_CUSTOMER_UI = "showAddCustomerUI",
    SHOW_ADD_NOTE_CUSTOMER_UI = "showAddNoteCustomerUI",
    SHOW_ADD_CUSTOM_FEE_UI = "showAddCustomFeeUI",
    SHOW_ADJUST_INVENTORY = "showAdjustInventory",
    SHOW_ADD_CUSTOM_SALE_UI = "showAddCustomSaleUI",
    SHOW_EDIT_CUSTOMERS = "showEditCustomers",
    SHOW_CREATE_CUSTOMERS = "showCreateCustomers",

    CASH_PAYMENT = "cashPayment",
    TAP_TO_PAY = "tapToPay",
    TERMINAL = "terminal",
    VENDARA = "vendara",
    CUSTOM_PAYMENT = "customPayment",

    FETCH_DATA = "fetchData",
    UPDATE_DATA = "updateData",

    RESET_FORM = "resetForm",
    SUBMIT_FORM = "submitForm",
    VALIDATE_FORM = "validateForm",

    ADD_STYLE = "addStyle",
    REMOVE_STYLE = "removeStyle",
    TOGGLE_STYLE = "toggleStyle",

    CREATE_STATE = "createState",
    UPDATE_STATE = "updateState",

    SEND_MESSAGE = "sendMessage",
    UPDATE_COMPONENT_PROPS = "updateProps",
    EXECUTE_FUNCTION = "executeFunction",

    CONDITION = "condition",
    HIDE = "hide",
    SHOW = "show",
    DISABLE = "disable",
    AUTHENTICATE = "authenticate",
    NOTIFICATION = "notification",
    CONFIRMATION = "confirmation",
    SPLIT_PAYMENT = "splitPayment",
    ADJUST_INVENTORY = "adjustInventory",
    ADD_CUSTOM_SALE = "addCustomSale",
    WOO_ORDER_STATUS = "wooOrderStatus",
    SHOPIFY_ORDER_STATUS = "shopifyOrderStatus",
    SWITCH_USER = "switchUser",
    SWITCH_USER_DIALOG = "switchUserDialog",
    SWITCH_USER_ROLE = "switchUserRole",
    SWITCH_USER_SPECIFIC = "switchUserSpecific",
    MOTO = "MOTO"
}

export enum PaymentStatus {
    FAILED = "failed",
    SUCCESS = "success",
    CANCELED = "canceled",
    IN_PROGRESS = "inProgress"
}

export interface Condition {
    id: string;
    name: string;
    conditions: string;
    actionId?: string;
}

export interface Action {
    id: string;
    name: ActionName;
    action: ActionDetails;
}
export interface BaseActionDetails<T extends ActionType> {
    type: T;
}
export interface DataActionDetails<T extends ActionType, D> extends BaseActionDetails<T> {
    data: D;
}
export interface TargetActionDetails<T extends ActionType> extends BaseActionDetails<T> {
    target: string;
}
export interface TargetDataActionDetails<T extends ActionType, D> extends TargetActionDetails<T> {
    data: D;
}
export type SHOW_UI_ACTION_TYPES =
    | ActionType.SHOW_ORDER_NOTE_UI
    | ActionType.SHOW_PROD_NOTE_UI
    | ActionType.CART_SHOW_DISCOUNT_UI
    | ActionType.CART_SHOW_PROD_DISCOUNT_UI
    | ActionType.SHOW_ADD_CUSTOM_FEE_UI
    | ActionType.SHOW_PROD_FEES_UI
    | ActionType.SHOW_ADD_CUSTOMER_UI
    | ActionType.SHOW_ADJUST_INVENTORY
    | ActionType.SHOW_ADD_CUSTOM_SALE_UI
    | ActionType.REFUND_SELECTED_ORDER
    | ActionType.REFUND_SCANNED_ORDER
    | ActionType.SHOW_ADD_NOTE_CUSTOMER_UI
    | ActionType.SHOW_CREATE_CUSTOMERS
    | ActionType.SHOW_EDIT_CUSTOMERS
    | ActionType.VIEW_PARK_ORDER;

export type ActionDetails =
    | BaseActionDetails<ActionType.CLOSE_MODAL>
    | BaseActionDetails<ActionType.HIDE>
    | BaseActionDetails<ActionType.SHOW>
    | BaseActionDetails<ActionType.DISABLE>
    | BaseActionDetails<ActionType.RESET_FORM>
    | BaseActionDetails<ActionType.NAVIGATE_TO_HOME>
    | BaseActionDetails<SHOW_UI_ACTION_TYPES>
    | DataActionDetails<ActionType.SUBMIT_FORM, Record<string, string>>
    | DataActionDetails<ActionType.CART_ADD_NOTE, string>
    | DataActionDetails<ActionType.CART_REMOVE_NOTE, string>
    | DataActionDetails<ActionType.CART_ADD_PRODUCT, string>
    | DataActionDetails<ActionType.CART_ADD_DISCOUNT, string>
    | DataActionDetails<ActionType.CART_REMOVE_PRODUCT, string>
    | DataActionDetails<ActionType.CART_REMOVE_DISCOUNT, string>
    | DataActionDetails<ActionType.PROD_ADD_NOTE, string>
    | DataActionDetails<ActionType.PROD_ADD_DISCOUNT, string>
    | DataActionDetails<ActionType.AUTHENTICATE, string>
    | TargetActionDetails<ActionType.OPEN_MODAL>
    | TargetActionDetails<ActionType.NAVIGATE_TO_URL>
    | TargetActionDetails<ActionType.NAVIGATE_TO_PAGE>
    | TargetDataActionDetails<ActionType.SEND_MESSAGE, string>
    | TargetDataActionDetails<ActionType.UPDATE_COMPONENT_PROPS, UpdateComponentProps>
    | DataActionDetails<ActionType.SWITCH_USER, { ids: string[] }>
    | DataActionDetails<ActionType.SWITCH_USER_DIALOG, { ids: string[] }>
    | DataActionDetails<ActionType.SWITCH_USER_ROLE, { ids: string[] }>
    | DataActionDetails<ActionType.SWITCH_USER_SPECIFIC, { id: string }>
    | BaseActionDetails<ActionType.RE_ORDER>;

export enum StockActions {
    RECEIVED = "STOCK_RECEIVED",
    RESTOCK = "RESTOCK_RETURN",
    REFUND_RESTOCK = "REFUND_RESTOCK_RETURN",
    DAMAGE = "DAMAGE",
    REFUND_DAMAGE = "REFUND_DAMAGE",
    THEFT = "THEFT",
    LOSS = "LOSS",
    RECOUNT = "INVENTORY_RECOUNT",
    SALE = "SALE"
}

export interface UpdateComponentProps {
    disable?: boolean;
    updateText?: string;

    updateStyle?: CSSProperties;

    addClass?: string[];
    removeClass?: string[];

    data?: Record<string, string>;
}

export type BuildComponentKey =
    | "baseLayerPage"
    | "baseLayerPopup"
    | "baseLayerCFD"
    | "globalBlockPrint"
    | "globalBlockCFD"
    | "div"
    | "repeaterDiv"
    | "templateDiv"
    | "text"
    | "icon"
    | "image"
    | "button"
    | "simpleCart"
    | "orderData"
    | "orderRefunds"
    | "unfixedCart"
    | "cashButton"
    | "printButton"
    | "receiptData"
    | "cashLedgerData"
    | "eosData"
    | "productQuantity"
    | "productVariations"
    | "customNumpad"
    | "search"
    | "tapToPay"
    | "terminalButton"
    | "motoButton"
    | "customPayment"
    | "homeMenuLink"
    | "productGrid"
    | "productList"
    | "searchField"
    | "dropdownField"
    | "radioField"
    | "slideout"
    | "embedSection"
    | "barcode"
    | "menu"
    | "iframe"
    | "vendaraPayment"
    | "orderList"
    | "paymentGrid"
    | "productSection"
    | "customerSection"
    | "customerNotes"
    | "customerList"
    | "orderSection"
    | "vismaElement"
    | "vismaPage"
    | "vismaInput"
    | "vismaButton"
    | "vismaButtonList"
    | "customSale"
    | "linkedInputDisplay"
    | "editableLabel"
    | "customSaleAdjustQuantity"
    | "orderSection"
    | "customSaleTaxConfiguration"
    | "customSaleAddToCartButton"
    | "divider"
    | "smartGrid"
    | "smartTile"
    | "cartSection";

export type MongoId = {
    $oid: string;
};
